import { all, put, call, takeLatest } from '@redux-saga/core/effects';
import {
  formatErrorMessage,
  rejectErrorCodeHelper,
} from '../../util/helpers/registerRejectErrorCodeHelper';
import {
  APPLICATION_TYPE_SETUP,
  CREATE_ACCOUNT_TYPE,
  CREATE_APPLICATION_STEP,
  DELETE_APPLICATION,
  GET_APPLICATION,
  PATCH_ACCOUNT_TYPE,
  COMPLETE_APPLICATION,
  CREATE_APPLICATION,
  UPGRADE_APPLICATION,
} from '../actions/application/applicationActionConstants';
import {
  applicationTypeSetupError,
  applicationTypeSetupSuccess,
  createAccountTypeError,
  createAccountTypeSuccess,
  createApplicationStepError,
  createApplicationStepSuccess,
  deleteApplicationError,
  deleteApplicationSuccess,
  getApplicationError,
  getApplicationSuccess,
  patchAccountTypeError,
  patchAccountTypeSuccess,
  completeApplicationError,
  createApplicationSuccess,
  createApplicationError,
  upgradeApplicationError,
} from '../actions/application/applicationActions';
import {
  getApplicationRequest,
  setupApplicationTypeRequest,
  deleteApplicationRequest,
  createApplicationStepRequest,
  createAccountTypeRequest,
  patchAccountTypeRequest,
  completeApplicationRequest,
  createApplicationRequest,
  upgradeApplicationRequest,
} from '../../request/applicationRequest';
import { accountTypeHelper } from '../../util/helpers/accountTypeHelper';
import {
  retrieveFromSessionStorage,
  storeInSessionStorage,
} from '../../util/helpers/sessionStorageHelper';
import { SIGNIFYD_SESSION_ID } from '../../constants/sessionStorage';
import loadSignifydDeviceFingerprintScript from '../../signifydDeviceFingerprint';

export function* fetchApplication({ payload }) {
  try {
    const { data } = yield call(getApplicationRequest, payload.data);
    if (data) {
      yield put(getApplicationSuccess(data));
      if (payload?.onSuccess) {
        yield call(payload?.onSuccess, data);
      }
    }
    yield put(getApplicationError());
  } catch (error) {
    const errorMessage = rejectErrorCodeHelper(error);
    yield put(getApplicationError(errorMessage));
  }
}

function* setupApplicationType({ payload }) {
  try {
    const { data } = yield call(setupApplicationTypeRequest, {
      ApplicationUid: payload.ApplicationUid,
      data: payload.data,
    });
    if (data) {
      yield put(applicationTypeSetupSuccess(data));
      yield call(payload?.onSuccess);
    }
  } catch (error) {
    const errorMessage = rejectErrorCodeHelper(error);
    yield put(applicationTypeSetupError(errorMessage));
  }
}

function* createApplicationAccountType({ payload }) {
  try {
    const { data } = yield call(createAccountTypeRequest, {
      ApplicationUid: payload.ApplicationUid,
      data: payload.data,
    });
    if (data) {
      yield put(createAccountTypeSuccess(data));
      yield call(accountTypeHelper, data.ApplicationType, data);
      yield call(payload?.onSuccess);
    }
  } catch (error) {
    const errorMessage = rejectErrorCodeHelper(error);
    yield put(createAccountTypeError(errorMessage));
  }
}

function* patchApplicationAccountType({ payload }) {
  try {
    const { data } = yield call(patchAccountTypeRequest, {
      ApplicationUid: payload.ApplicationUid,
      data: payload.data,
    });
    if (data) {
      yield put(patchAccountTypeSuccess(data));
      yield call(accountTypeHelper, data.ApplicationType, data);
      yield call(payload?.onSuccess);
    }
  } catch (error) {
    const errorMessage = rejectErrorCodeHelper(error);
    yield put(patchAccountTypeError(errorMessage));
  }
}

function* deleteApplication({ payload }) {
  try {
    const { data } = yield call(deleteApplicationRequest, {
      ApplicationUid: payload.ApplicationUid,
    });
    if (data) {
      yield put(deleteApplicationSuccess(data));
    }
  } catch (error) {
    const errorMessage = rejectErrorCodeHelper(error);
    yield put(deleteApplicationError(errorMessage));
  }
}

function* createApplicationStep({ payload }) {
  try {
    yield call(createApplicationStepRequest, {
      ApplicationUid: payload.ApplicationUid,
      data: payload.data,
    });

    yield put(createApplicationStepSuccess(payload.data));
    yield call(payload.onSuccess);
  } catch (error) {
    const errorMessage = rejectErrorCodeHelper(error);
    yield put(createApplicationStepError(errorMessage));
  }
}

function* createApplication({ payload }) {
  try {
    const { data } = yield call(createApplicationRequest, {
      ApplicationUid: payload.ApplicationUid,
      data: payload.data,
      params: payload.params,
    });

    yield put(createApplicationSuccess(data));
    yield call(payload.onSuccess, data.ApplicationUid);
  } catch (error) {
    const errorMessage = rejectErrorCodeHelper(error);
    yield put(createApplicationError(errorMessage));
  }
}

function* completeApplication({ payload }) {
  try {
    const sessionId = retrieveFromSessionStorage(SIGNIFYD_SESSION_ID);
    const { data } = yield call(completeApplicationRequest, {
      ApplicationUid: payload.ApplicationUid,
      sessionId,
    });
    // load Signifyd script again with different order session id
    loadSignifydDeviceFingerprintScript();
    const script = document.getElementById('sig-api');
    storeInSessionStorage(SIGNIFYD_SESSION_ID, script.dataset.orderSessionId);
    // eslint-disable-next-line no-console
    console.log(
      'CREATED SIGNIFYD_SESSION_ID ON COMPLETE:',
      script.dataset.orderSessionId,
    );
    if (data) {
      yield call(payload.onSuccess, data);
    }
  } catch (error) {
    const errorMessage = formatErrorMessage(error);
    yield put(completeApplicationError(errorMessage));
    yield call(payload.onError, errorMessage);
  }
}

function* upgradeApplication({ payload }) {
  try {
    const { data } = yield call(upgradeApplicationRequest, {
      applicationUid: payload.ApplicationUid,
    });

    if (data) {
      yield call(payload.onSuccess, data);
    }
  } catch (error) {
    const errorMessage = formatErrorMessage(error);
    yield put(upgradeApplicationError(errorMessage));
    yield call(payload.onError, errorMessage);
  }
}

export default function* applicationSaga() {
  return yield all([
    takeLatest(GET_APPLICATION, fetchApplication),
    takeLatest(APPLICATION_TYPE_SETUP, setupApplicationType),
    takeLatest(CREATE_ACCOUNT_TYPE, createApplicationAccountType),
    takeLatest(PATCH_ACCOUNT_TYPE, patchApplicationAccountType),
    takeLatest(DELETE_APPLICATION, deleteApplication),
    takeLatest(CREATE_APPLICATION_STEP, createApplicationStep),
    takeLatest(CREATE_APPLICATION, createApplication),
    takeLatest(COMPLETE_APPLICATION, completeApplication),
    takeLatest(UPGRADE_APPLICATION, upgradeApplication),
  ]);
}
